import React, { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Crown, Sparkles, Calendar, Lock, Unlock, ShieldCheck, CreditCard,
  X, Check, Rocket, Heart,
} from "lucide-react";
import { CaptionPack } from "./FreeVsProReel";

/**
 * /share/try-pro-free — 14-day FREE trial conversion reel.
 *
 * Four 9:16 scenes:
 *   Scene 1 — "14 days FREE Pro — no risk" big hero
 *   Scene 2 — "Here's what you actually unlock" benefit grid
 *   Scene 3 — "No risk, no auto-charge if you cancel" trust panel
 *   Scene 4 — QR + clear CTA
 *
 * Caption pack underneath with 3 trial-focused angles.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const TRIAL_URL = `${PUBLIC_URL}/register?ref=try-pro-free`;

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
        style={{
          aspectRatio: "9 / 16",
          maxWidth: 480,
          backgroundImage: bg,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid={testid}
      >
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
          aria-hidden="true"
        >
          🌐 camp-compass.uk
        </div>
      </section>
    </div>
  );
}

export default function TryProFreeReel() {
  const captions = useMemo(() => [
    {
      title: "Caption · 14-day trial signature post",
      body: `🎁 14 DAYS OF PRO. ZERO RISK.

You sign up today → 14 full days of CampCompass Pro, on us.

🤖 Camper AI (ask anything, any time)
🎒 AI packing list for THAT trip
🌦 Storm alerts before you leave
📅 Date-based availability search
💸 Partner discount codes
📸 Photo uploads to the Trip Wall

If you love it, £1.99/mo. If not, cancel in one tap. Zero auto-charge ambush.

Why are you still scrolling? 👉 camp-compass.uk

#ukcamping #campingweekend #campervan #ukcamping2026`,
    },
    {
      title: "Caption · Trust-builder for cautious campers",
      body: `Here's the deal 👇

14 days of CampCompass Pro, FREE.

✓ No upfront charge
✓ No card needed to browse
✓ ONE tap to cancel
✓ Reminder email 48h before any charge

We don't believe in dark patterns. If Pro doesn't make YOUR camping life easier, you walk. No drama.

👉 Try it: camp-compass.uk

#ukcamping #campsiteuk`,
    },
    {
      title: "Caption · Short hook for stories",
      body: `14 days of Pro 🎁
Free. Cancel anytime.

Storm alerts. AI advice. Packing lists. Discount codes.

👉 camp-compass.uk

#ukcamping`,
    },
  ], []);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="try-pro-free-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-2">
          <Crown className="w-3.5 h-3.5" /> 14-day trial reel
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">14 days of Pro.<br />Zero risk.</h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          Four 9:16 scenes focused entirely on getting the visitor to start the 14-day trial.
          Lead with the offer, prove the value, kill the risk, send to the signup.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 px-3 pb-10 max-w-7xl mx-auto justify-items-center">

        {/* SCENE 1 — Big offer hero */}
        <Scene
          label="Scene 1 · the offer"
          testid="tp-scene-1"
          bg='linear-gradient(180deg, rgba(255,69,0,0.5) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.9) 100%), url("https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop")'
        >
          <div className="p-6 pt-10 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Crown className="w-3.5 h-3.5" /> Pro · 14 days
            </Sticker>
            <Sticker color="#ff4500">FREE</Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="text-sm mono uppercase tracking-[0.25em] text-white/90 mb-2">
              The 14-day trial
            </div>
            <div className="font-display text-[100px] sm:text-[140px] leading-[0.85] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
              14
            </div>
            <div className="font-display text-[28px] sm:text-[36px] leading-tight -mt-2">
              DAYS of PRO
            </div>
            <div className="inline-block font-display text-2xl sm:text-3xl px-3 py-1 mt-3" style={{ background: "#ff4500", color: "#fff" }}>
              On us. Zero risk.
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-xs mono uppercase tracking-[0.22em] text-white/85">
              Sign up today — keep scrolling 👇
            </div>
          </div>
        </Scene>

        {/* SCENE 2 — What you unlock */}
        <Scene
          label="Scene 2 · what you unlock"
          testid="tp-scene-2"
          bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)"
        >
          <div className="p-6 pt-10">
            <Sticker color="#ff4500">
              <Unlock className="w-3.5 h-3.5" /> What you unlock
            </Sticker>
          </div>
          <div className="px-5">
            <div className="font-display text-[36px] sm:text-[44px] leading-[0.95] mb-4 text-center">
              The things<br />you actually use.
            </div>
            <div className="space-y-2">
              <UnlockRow label="🤖 Camper AI" body="Ask any trip question, any time" />
              <UnlockRow label="🎒 AI packing list" body="Bespoke to your trip + weather" />
              <UnlockRow label="🌦 Storm alerts" body="48h heads-up before you leave" />
              <UnlockRow label="📅 Date-based search" body="Shows only available pitches" />
              <UnlockRow label="💸 Partner deals" body="Discount codes from outdoor brands" />
              <UnlockRow label="📸 Photo uploads" body="Post your trip on the Wall" />
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-xs mono uppercase tracking-[0.22em] text-[#ffe7d6]">
              All FREE for 14 days
            </div>
          </div>
        </Scene>

        {/* SCENE 3 — Trust panel */}
        <Scene
          label="Scene 3 · zero risk"
          testid="tp-scene-3"
          bg="linear-gradient(180deg, #0a4a0a 0%, #0e6e0e 50%, #0a4a0a 100%)"
        >
          <div className="p-6 pt-10">
            <Sticker color="#ffffff" textColor="#0a4a0a">
              <ShieldCheck className="w-3.5 h-3.5" /> Zero risk promise
            </Sticker>
          </div>
          <div className="px-6">
            <div className="font-display text-[42px] sm:text-[52px] leading-[0.95] mb-5 text-center">
              No dark<br />patterns.
            </div>
            <div className="space-y-3">
              <TrustRow icon={<Check className="w-5 h-5" />} text="Reminder email 48h before any charge" />
              <TrustRow icon={<Check className="w-5 h-5" />} text="Cancel in ONE tap — no phone calls" />
              <TrustRow icon={<Check className="w-5 h-5" />} text="Free while we grow · browse the full app" />
              <TrustRow icon={<Check className="w-5 h-5" />} text="£1.99/mo if you stay — less than a coffee" />
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-xs mono uppercase tracking-[0.22em] text-white/95">
              We back ourselves
            </div>
          </div>
        </Scene>

        {/* SCENE 4 — Start now QR */}
        <Scene
          label="Scene 4 · start now"
          testid="tp-scene-4"
          bg='linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.85) 100%), url("https://images.unsplash.com/photo-1455587734955-081b22074882?w=1080&h=1920&fit=crop&auto=format&q=80")'
        >
          <div className="p-6 pt-10 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Rocket className="w-3.5 h-3.5" /> Start now
            </Sticker>
            <Sticker color="#ff4500">
              <Heart className="w-3.5 h-3.5" /> 14 days FREE
            </Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="font-display text-[44px] sm:text-[58px] leading-[0.92] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
              Sign up.<br />Start free.
            </div>
            <div className="inline-block font-display text-xl sm:text-2xl px-3 py-1 mt-3" style={{ background: "#ff4500", color: "#fff" }}>
              20-second sign up
            </div>
          </div>
          <div className="p-5 space-y-3" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.88) 100%)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-left min-w-0">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/90 mb-1">Scan or visit</div>
                <div className="font-display text-base sm:text-lg leading-tight break-all">camp-compass.uk</div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-[#ffe7d6] mt-1">Cancel anytime · no charge ambush</div>
              </div>
              <div className="bg-white p-1.5 shrink-0" data-testid="tp-qr">
                <QRCodeSVG value={TRIAL_URL} size={104} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
              </div>
            </div>
          </div>
        </Scene>
      </div>

      <CaptionPack captions={captions} testid="tp-captions" />
    </div>
  );
}

function UnlockRow({ label, body }) {
  return (
    <div className="flex items-baseline gap-2 border-b border-white/15 pb-2">
      <span className="font-display text-sm sm:text-base flex-shrink-0">{label}</span>
      <span className="text-[11px] sm:text-xs text-white/80 truncate">— {body}</span>
    </div>
  );
}

function TrustRow({ icon, text }) {
  return (
    <div className="flex items-start gap-2 bg-white/10 backdrop-blur p-2.5">
      <div className="flex-shrink-0 mt-0.5 text-white">{icon}</div>
      <div className="text-sm leading-snug font-medium">{text}</div>
    </div>
  );
}
